import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import RichText from "components/elements/richText"

const CloseIcon = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <path
      d="m6 6 12 12M18 6 6 18"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.8"
    />
  </svg>
)

const MailIcon = () => (
  <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4.2-8 4.8-8-4.8V6l8 4.8L20 6v2.2Z" />
  </svg>
)

const PhoneIcon = () => (
  <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
    <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2Z" />
  </svg>
)

const BioModal = ({ doctor, onClose, tone }) => {
  const dialogRef = useRef(null)
  const closeRef = useRef(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement
    closeRef.current?.focus()

    // Stop the page behind the dialog from scrolling
    const { overflow } = document.body.style
    document.body.style.overflow = "hidden"

    const onKeyDown = event => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = overflow
      previouslyFocused?.focus?.()
    }
  }, [onClose])

  return (
    <div
      className="ClinicTeam__overlay"
      onClick={event => {
        // Only a click on the backdrop itself closes the dialog
        if (event.target === event.currentTarget) onClose()
      }}
      role="presentation"
    >
      <div
        aria-labelledby="bio-modal-name"
        aria-modal="true"
        className={`ClinicTeam__modal ClinicTeam__modal--${tone}`}
        ref={dialogRef}
        role="dialog"
      >
        <button
          aria-label={`Close ${doctor.name}'s biography`}
          className="ClinicTeam__modal-close"
          onClick={onClose}
          ref={closeRef}
          type="button"
        >
          <CloseIcon />
        </button>

        <div className="ClinicTeam__modal-head">
          {doctor.picture?.fluid?.src && (
            <img
              alt=""
              className="ClinicTeam__modal-photo"
              src={doctor.picture.fluid.src}
            />
          )}

          <div className="ClinicTeam__modal-identity">
            <p className="ClinicTeam__modal-name" id="bio-modal-name">
              {doctor.name}
            </p>
            {doctor.role && (
              <p className="ClinicTeam__modal-role">{doctor.role}</p>
            )}

            {doctor.email && (
              <p className="ClinicTeam__modal-contact">
                <MailIcon />
                <a href={`mailto:${doctor.email}`}>{doctor.email}</a>
              </p>
            )}
            {doctor.phone && (
              <p className="ClinicTeam__modal-contact">
                <PhoneIcon />
                <a href={`tel:${doctor.phone.replace(/[^\d+]/g, "")}`}>
                  {doctor.phone}
                </a>
              </p>
            )}
          </div>
        </div>

        {doctor.details?.json && (
          <div className="ClinicTeam__modal-bio">
            <RichText richText={doctor.details} textStyle="full" />
          </div>
        )}
      </div>
    </div>
  )
}

BioModal.propTypes = {
  doctor: PropTypes.shape({
    details: PropTypes.shape({ json: PropTypes.object }),
    email: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    picture: PropTypes.shape({
      fluid: PropTypes.shape({ src: PropTypes.string }),
    }),
    role: PropTypes.string,
  }),
  onClose: PropTypes.func,
  tone: PropTypes.string,
}

export default BioModal
